import { Input, Select } from "antd";
import { Quote } from "lucide-react";

import CodeSectionPanel, { CodeSectionReference } from "components/code-section-panel/code-section-panel";
import { PlanReviewCandidateMatchKind, PlanReviewCandidateResolution } from "constants/field-values";
import { PLAN_REVIEW_RESOLUTION_OPTIONS, PLAN_REVIEW_SEVERITY_OPTIONS } from "constants/constants";

const MATCH_META: Record<string, { label: string; className: string; hint: string }> = {
	[PlanReviewCandidateMatchKind.NEW]: {
		label: "New",
		className: "prcc-badge-new",
		hint: "Nothing on file uses this code."
	},
	[PlanReviewCandidateMatchKind.CHANGED]: {
		label: "Changed",
		className: "prcc-badge-changed",
		hint: "A rule with this code exists and the wording differs."
	},
	[PlanReviewCandidateMatchKind.IDENTICAL]: {
		label: "Unchanged",
		className: "prcc-badge-identical",
		hint: "A rule with this code exists and says the same thing."
	}
};

// "Keep both" only makes sense when there is something to keep it next to.
const resolutionsFor = (matchKind: string) =>
	matchKind === PlanReviewCandidateMatchKind.NEW
		? PLAN_REVIEW_RESOLUTION_OPTIONS.filter(
				o => o.value === PlanReviewCandidateResolution.IMPORT || o.value === PlanReviewCandidateResolution.SKIP
			)
		: PLAN_REVIEW_RESOLUTION_OPTIONS.filter(o => o.value !== PlanReviewCandidateResolution.IMPORT);

export interface CandidateCardData {
	id: string;
	title: string;
	description: string;
	code?: string | null;
	category_id?: string | null;
	default_severity?: string | null;
	source_quote?: string | null;
	page_number?: number | null;
	confidence?: number | null;
	match_kind: string;
	resolution: string;
	imported_requirement_id?: string | null;
	document?: { file_name: string } | null;
	matched_requirement?: { description: string } | null;
	/** The code sections the extraction's citations resolved to. Shown before the import
	 *  because they are what the analysis will read alongside the rule. */
	plan_review_candidate_code_sections?: CodeSectionReference[];
	plan_review_candidate_submission_types: { submission_type_id: string }[];
}

interface PlanReviewCandidateCardProps {
	candidate: CandidateCardData;
	submissionTypes: { id: string; name: string }[];
	categoryOptions: { value: string; label: string }[];
	readOnly?: boolean;
	onFieldChange: (candidate: { id: string }, field: string, value: unknown) => void;
	onSubmissionTypesChange: (candidate: { id: string }, ids: string[]) => void;
}

/**
 * One rule the extraction is proposing, as its own editable card.
 *
 * A table forced every rule into one row, which does not fit a paragraph-long
 * description plus the sentence it was read from plus a side-by-side comparison. Each
 * candidate gets the height it needs, and the layout collapses to one column on a
 * narrow screen.
 */
const PlanReviewCandidateCard = ({
	candidate,
	submissionTypes,
	categoryOptions,
	readOnly = false,
	onFieldChange,
	onSubmissionTypesChange
}: PlanReviewCandidateCardProps) => {
	const meta = MATCH_META[candidate.match_kind] ?? MATCH_META[PlanReviewCandidateMatchKind.NEW];
	const isSkipped = candidate.resolution === PlanReviewCandidateResolution.SKIP;

	return (
		<article className={`prcc-card ${isSkipped ? "prcc-card-skipped" : ""}`.trim()}>
			<header className="prcc-head">
				<span className={`prcc-badge ${meta.className}`} title={meta.hint}>
					{meta.label}
				</span>
				{candidate.code ? <span className="prcc-code">{candidate.code}</span> : null}
				{candidate.confidence != null ? (
					<span className="prcc-confidence">{Math.round(Number(candidate.confidence) * 100)}% confident</span>
				) : null}
				{candidate.imported_requirement_id ? <span className="prcc-imported">Imported</span> : null}
			</header>

			<div className="prcc-body">
				<div className="prcc-field">
					<label className="prcc-label">Title</label>
					<Input
						className="prcc-control"
						defaultValue={candidate.title}
						disabled={readOnly}
						onBlur={e => e.target.value !== candidate.title && onFieldChange(candidate, "title", e.target.value)}
					/>
				</div>

				<div className="prcc-field">
					<label className="prcc-label">What the package must satisfy</label>
					<Input.TextArea
						className="prcc-control prcc-textarea"
						rows={3}
						defaultValue={candidate.description}
						disabled={readOnly}
						onBlur={e =>
							e.target.value !== candidate.description && onFieldChange(candidate, "description", e.target.value)
						}
					/>
				</div>

				{candidate.source_quote ? (
					<p className="prcc-quote">
						<Quote size={12} />
						<span>
							{candidate.source_quote}
							<span className="prcc-quote-source">
								{" — "}
								{candidate.document?.file_name ?? "from your instructions"}
								{candidate.page_number ? `, p. ${candidate.page_number}` : ""}
							</span>
						</span>
					</p>
				) : null}

				{candidate.plan_review_candidate_code_sections?.length ? (
					<div className="prcc-code-sections">
						<p className="prcc-code-label">Code sections this rule cites</p>
						<CodeSectionPanel references={candidate.plan_review_candidate_code_sections} />
					</div>
				) : null}

				{/* What the reviewer needs to choose between replacing and keeping both */}
				{candidate.matched_requirement ? (
					<div className="prcc-diff">
						<div className="prcc-diff-side">
							<p className="prcc-diff-label">On file</p>
							<p className="prcc-diff-text">{candidate.matched_requirement.description}</p>
						</div>
						<div className="prcc-diff-side">
							<p className="prcc-diff-label">Proposed</p>
							<p className="prcc-diff-text">{candidate.description}</p>
						</div>
					</div>
				) : null}

				<div className="prcc-classification">
					<div className="prcc-field">
						<label className="prcc-label">Review category</label>
						<Select
							className="prcc-control"
							value={candidate.category_id ?? undefined}
							placeholder="Pick a category"
							disabled={readOnly}
							options={categoryOptions}
							onChange={value => onFieldChange(candidate, "category_id", value)}
						/>
					</div>
					<div className="prcc-field">
						<label className="prcc-label">Severity</label>
						<Select
							className="prcc-control"
							value={candidate.default_severity ?? undefined}
							placeholder="Severity"
							disabled={readOnly}
							options={PLAN_REVIEW_SEVERITY_OPTIONS.map(o => ({ label: o.label, value: o.value }))}
							onChange={value => onFieldChange(candidate, "default_severity", value)}
						/>
					</div>
					<div className="prcc-field prcc-field-wide">
						<label className="prcc-label">Submission types</label>
						<Select
							className="prcc-control"
							mode="multiple"
							placeholder="Which submittals is this rule evaluated for?"
							disabled={readOnly}
							value={candidate.plan_review_candidate_submission_types.map(t => t.submission_type_id)}
							options={submissionTypes.map(t => ({ label: t.name, value: t.id }))}
							onChange={ids => onSubmissionTypesChange(candidate, ids)}
						/>
					</div>
				</div>
			</div>

			{!readOnly && (
				<footer className="prcc-footer">
					<span className="prcc-label">Decision</span>
					<div className="prcc-resolution">
						{resolutionsFor(candidate.match_kind).map(option => (
							<button
								key={option.value}
								type="button"
								className={`prcc-resolution-btn ${
									candidate.resolution === option.value ? "prcc-resolution-btn-active" : ""
								}`}
								onClick={() => onFieldChange(candidate, "resolution", option.value)}
							>
								{option.label}
							</button>
						))}
					</div>
				</footer>
			)}
		</article>
	);
};

export default PlanReviewCandidateCard;
